import { cn } from '@/utlis/cn'
import { logger as rootLogger } from '@/services/Logger'
import { isMuseAudioShareInputDevice, isMuseDAWBridgeDevice } from '@/services/MediaDevices'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { UserContext, UserContextType, useUserContext } from '@/UserContext'
import Sidebar from '@/pages/SessionPage/internal/sidebar/Sidebar'
import { AppContext, AppContextType, ExternalEvent, useAppContext } from '@/AppContext'
import useCentralizedChatManager from '@/components/useCentralizedChatManager'
import FakeMediaStreamTrack from '@/core/AudioEngine/FakeMediaStreamTrack'
import { ExternalEventEmitter } from '@/ExternalEventEmitter'
import useSkipbounce from '@/hooks/useSkipbounce'
import { getUsername, RoomContext, RoomContextState, RoomContextType, useRoomContext } from '@/RoomContext'
import ElectronIPC from '@/services/ElectronIPC'
import { persistent } from '@/services/LocalStorage'
import useAppContextRef from '@/services/useAppContextRef'
import useCollaboratePageWindowPolicy from '@/services/useCollaboratePageWindowPolicy'
import useMiscRoomActions from '@/services/useMiscRoomActions'
import useRoomActions from '@/services/useRoomActions'
import useRoomContextRef from '@/services/useRoomContextRef'
import useRoomViews from '@/services/useRoomViews'
import useRootSnackbar from '@/services/useRootSnackbar'
import useSimpleRecordingInitialNoneAudioOutputFix from '@/services/useSimpleRecordingInitialNoneAudioOutputFix'
import { arrayDiff, isNullMediaDeviceId } from '@/utils'
import { useHistory } from 'react-router-dom'
import { serializeError } from 'serialize-error'
import { PerfStatsEmitter } from '@/pages/PerfStatsPage/PerfStatsEmitter'
import * as Constants from '@/services/Constants'
import CollaboratePageDialogs from '@/pages/CollaboratePage/CollaboratePageDialogs'
import { FloatingTopStatusBar } from '@/components/FloatingTopStatusBar/FloatingTopStatusBar'
import { ModifiedLiveKitAudioRenderer } from '@/UIComponents/ModifiedLiveKitAudioRenderer'
import AudioComponent from '@/pages/CollaboratePage/Audio'
import { Gallery } from '@/pages/SessionPage/internal/gallery/Gallery'
import { Toolbar } from '@/pages/SessionPage/internal/toolbar/Toolbar'
import { sessionViewModeState, useViewMode } from './internal/shared/useViewMode'
import { SessionSettingsDialog } from '@/components/SessionSettings/SessionSettingsDialog'
import { SettingsLocation } from '@/components/SessionSettings/SessionSettings'
import { Trigger } from '@/utlis/Trigger'
import { useNonIdempotentEffect } from '@/hooks/useNonIdempotentEffect'
import { PortalModal } from '../PortalModal'
import { env } from '@/services/EnvironmentVariables'
import { throttle } from 'lodash'
import useChatRoomController from '../ChatRoom/useChatRoomController'
import useAppNotificationActions from '@/functionalities/AppNotification/useAppNotificationActions'
import { useTranslation } from 'react-i18next'
import { isSidebarRetractedOnStart } from '@/Logic'

const log = rootLogger.createChildLogger({ name: 'SessionPage' })

export const sessionSettingsTrigger = new Trigger<SettingsLocation>()

export default function SessionPage() {
    const history = useHistory()

    // --- contexts
    const { state, dispatch } = useContext(UserContext) as UserContextType
    const { state: appState } = useContext(AppContext) as AppContextType
    const { state: roomState, dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    // --- context refs
    const { appStateRef } = useAppContextRef()
    const { roomStateRef } = useRoomContextRef()

    const chatManager = useCentralizedChatManager({
        userUrn: state.userUrn,
        roomUrn: roomState.roomUrn || '',
        roomType: 'room',
        isEnabled: true,
    })

    const chatController = useChatRoomController(chatManager)

    useEffect(() => {
        if (roomState.myWaitlistStatus === 'isAccepted') {
            chatManager.loadMessages()
        }
    }, [roomState.myWaitlistStatus])

    useSimpleRecordingInitialNoneAudioOutputFix({
        currPeerAudioOutputStream: roomState.currPeerAudioOutputStreamForScreenRecording,
    })

    useCollaboratePageWindowPolicy()

    const talkbackKeyState = roomState.isTalkbackKeyPressed
    const set_talkbackKeyState = useCallback(
        (isPressed: boolean) => {
            roomDispatch({
                type: 'update_room_context',
                value: { isTalkbackKeyPressed: isPressed },
            })
        },
        [roomDispatch]
    )

    const {
        joinRoom,
        destroyRoom,
        talkbackKeyStateChangeHandler,
        calcSessionRecordingRemotePeers,
        calcCurrRemoteRecordingPeerAudio,
        endCurrRemoteAudioRecord,
        leaveRoomStart,
        screenRecordingCommand,
    } = useRoomActions()

    const { openSettingsModal, closeSettingsModal } = useMiscRoomActions()

    useEffect(() => {
        const h = (location: SettingsLocation | undefined) => {
            openSettingsModal(location?.tab, location?.section)
        }
        sessionSettingsTrigger.on('trigger', h)
        return () => {
            sessionSettingsTrigger.off('trigger', h)
        }
    }, [openSettingsModal])

    const { isPresenting } = useRoomViews()

    const isPresentingRef = useRef(false)
    useEffect(() => {
        isPresentingRef.current = isPresenting
    }, [isPresenting])

    // Lock MainWindow from closing and get event when app is closed by the user.
    const onWindowClosing = () => {
        log.info('onWindowClosing', 'called.')
        try {
            leaveRoomStart({ endSession: false })
        } catch (e) {
            ElectronIPC.quitApp()
        }
    }

    useNonIdempotentEffect(() => {
        if (!roomState.preRoomUrn && !roomState.roomUrn) {
            history.push('/')
            return
        }

        rootLogger.event({ page: 'session-page', action: 'enter' })

        roomDispatch({
            type: 'update_room_context',
            value: {
                sessionStartedTime: new Date(),
            },
        })

        if (isSidebarRetractedOnStart(state, roomState)) {
            sessionViewModeState.setSidebarCollapsed(true)
        }

        ElectronIPC.lockMainWindowClosing(true, onWindowClosing)

        ElectronIPC.setMenu({ menuName: 'session' })

        ElectronIPC.setPowerSaveBlocker(true)

        if (roomState.preRoomUrn) {
            joinRoom(roomState.preRoomUrn)
        } else if (!roomState.roomUrn) {
            log.assert('load', 'roomUrn is null!')
        }

        const unhistoryListen = history.listen(() => {
            rootLogger.event({ page: 'collaborate-page', action: 'exit' })

            // deregister
            // The "null" means, just deregister the handler; ChooseInitialPage will call its
            // own `lockMainWindowClosing()`.
            ElectronIPC.lockMainWindowClosing(null, onWindowClosing)

            destroyRoom()
        })

        return () => {
            unhistoryListen()
        }
    }, [])

    useExternalEventHandler(isPresentingRef, set_talkbackKeyState)

    // Register key events for shortcut handling (browser keys to fall back on in case iohook doesn't work.
    // These keys will only work when the app is on focus.)
    useEffect(() => {
        let wasTalkbackKeyDown = false
        function handleKeyDown(e: KeyboardEvent) {
            const talkbackKey = persistent.getObject(Constants.keyboardshortcut_talkback)?.browser || null

            if (env.IOHOOK_VERBOSE_LOG) {
                console.log(`Browser keydown: e.code=${e.code} e.key=${e.key} e.keyCode=${e.keyCode}`)
            }

            if (appStateRef.current.appSettings.talkbackKeyMode === 'hold') {
                // talkback "hold" mode
                if (e.code === talkbackKey) {
                    if (wasTalkbackKeyDown === false) {
                        set_talkbackKeyState(true)
                        wasTalkbackKeyDown = true
                    }
                }
            } else {
                // talkback "toggle" mode
                // no-op: let iohook handle it
            }
        }
        function handleKeyUp(e: KeyboardEvent) {
            if (appStateRef.current.appSettings.talkbackKeyMode === 'hold') {
                // talkback "hold" mode
                const talkbackKey = persistent.getObject(Constants.keyboardshortcut_talkback)?.browser || null
                if (e.code === talkbackKey) {
                    set_talkbackKeyState(false)
                    wasTalkbackKeyDown = false
                }
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('keyup', handleKeyUp)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('keyup', handleKeyUp)
        }
    }, [])

    const talkbackKeyState_debounce = useSkipbounce(talkbackKeyState, 150)
    useEffect(() => {
        if (talkbackKeyState_debounce) {
            // true means talkback key "down".
            talkbackKeyStateChangeHandler(true)
        } else {
            talkbackKeyStateChangeHandler(false)
        }
        // NOTE: do not include `talkbackKeyStateChangeHandler` in the dependency array
        // because it will cause an unintended talkback state update when the callback's
        // dependency is updated.
    }, [talkbackKeyState_debounce])

    useRemoteRecordingManagement()

    useEffect(
        function currPeerAudioOutputStreamStasis() {
            const incomingP2pAudios = Object.keys(roomState.peers).flatMap(
                (peerSessionId) => roomState.peers[peerSessionId].remoteP2pAudios
            )

            // set currPeerAudioOutputStream in RoomContext, which consists only of tracks
            // in `incomingP2pAudios`.
            {
                const existingAudioTracks = roomStateRef.current.currPeerAudioOutputStream
                    ? roomStateRef.current.currPeerAudioOutputStream.getAudioTracks()
                    : []

                const { added: addedTracks } = arrayDiff(
                    incomingP2pAudios,
                    existingAudioTracks,
                    (a, b) => a.track.id === b.id
                )

                if (addedTracks.length > 0) {
                    const mediaStreamNext = new MediaStream(existingAudioTracks)
                    for (let added of addedTracks) {
                        mediaStreamNext.addTrack(added.track)
                    }
                    roomDispatch({
                        type: 'update_room_context',
                        value: { currPeerAudioOutputStream: mediaStreamNext },
                    })
                }
            }

            // set currPeerAudioOutputStreamForScreenRecording, which consists of tracks both in
            // incoming p2p and sfu remote peer audios.
            const mediaStreamNext = (() => {
                const mediaStream = roomStateRef.current.currPeerAudioOutputStreamForScreenRecording
                const incomingSfuAudios = Object.keys(roomState.peers).flatMap(
                    (peerSessionId) => roomState.peers[peerSessionId].remoteSfuAudios
                )
                const existingAudioTracksP2pAndSfu = mediaStream ? mediaStream.getAudioTracks() : []

                const { added: addedTracksP2pAndSfu } = arrayDiff(
                    [...incomingP2pAudios, ...incomingSfuAudios],
                    existingAudioTracksP2pAndSfu,
                    (a, b) => a.track.id === b.id
                )

                if (addedTracksP2pAndSfu.length > 0) {
                    const mediaStreamNext = new MediaStream(existingAudioTracksP2pAndSfu)
                    for (let added of addedTracksP2pAndSfu) {
                        mediaStreamNext.addTrack(added.track)
                    }

                    return mediaStreamNext
                } else {
                    return mediaStream
                }
            })()
            if (mediaStreamNext !== roomStateRef.current.currPeerAudioOutputStreamForScreenRecording) {
                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        currPeerAudioOutputStreamForScreenRecording: mediaStreamNext,
                    },
                })
            }
        },
        [roomState.peers, roomDispatch, roomStateRef]
    )

    useEffect(
        function resetMuseSystemAudioVolume() {
            if (env.OS !== 'darwin' || !env.IS_ELECTRON) {
                return
            }

            state.mediaSources.audioInputs.forEach((a) => {
                if (isMuseAudioShareInputDevice(a) || isMuseDAWBridgeDevice(a)) {
                    if (a.nativeDeviceId != null) {
                        ElectronIPC.setSystemDeviceVolume({
                            nativeDeviceId: a.nativeDeviceId,
                            value: 1,
                            isInput: false,
                        })
                    } else {
                        log.error(
                            'resetMuseSystemAudioVolume',
                            'failed to reset Muse System Audio ' + 'volume to 1 because the nativeDeviceId was null.',
                            { audioInputDevice: a }
                        )
                    }
                }
            })
        },
        [state.mediaSources]
    )

    PerfStatsEmitter.inc('collaboratePageRenderCt')

    // TEST: test render FloatingTopStatusBar
    // const [ tmpIsScreenRecording, setTmpIsScreenRecording ] = useState(false)

    const viewMode = useViewMode()
    useGalleryLayoutObserver(roomState)

    // NOTE use 'minmax' so that the child elements do not grow the grid
    // beyond the container.
    const columns = useMemo(() => {
        if (viewMode.sidebarPoppedOut) {
            return 'minmax(0, 1fr)'
        }
        if (viewMode.galleryHidden) {
            return 'minmax(0, 1fr)'
        }
        if (viewMode.sidebarCollapsed) {
            return 'minmax(0, 1fr) min-content'
        }
        return 'minmax(0, 1fr) 330px'
    }, [viewMode])

    const renderToolbarInSidebar = !viewMode.sidebarCollapsed && (viewMode.galleryHidden || viewMode.sidebarPoppedOut)

    return (
        <div id="session-page" className={cn('relative w-screen h-dvh overflow-hidden')}>
            <div className={cn('grid w-full h-full overflow-hidden min-w-0')} style={{ gridTemplateColumns: columns }}>
                {!viewMode.galleryHidden && (
                    <div className="relative flex flex-col items-between justify-center min-h-0">
                        <Gallery />
                        {!viewMode.sidebarPoppedOut && !viewMode.fullscreen && <Toolbar />}
                    </div>
                )}
                {viewMode.sidebarPoppedOut ? (
                    <PortalModal
                        frameName="SidebarModal"
                        onClose={closeSettingsModal}
                        onDocument={(document: Document) => {
                            setTimeout(() => {
                                sessionViewModeState.setSidebarPoppedOutDocument(document)
                            }, 0)
                        }}
                        noTransition
                    >
                        <Sidebar reserveToolbarSpace={renderToolbarInSidebar} chatController={chatController} />
                    </PortalModal>
                ) : (
                    <>
                        <Sidebar reserveToolbarSpace={renderToolbarInSidebar} chatController={chatController} />
                    </>
                )}
            </div>

            <CollaboratePageDialogs />

            <SessionSettingsDialog inSession={true} />

            {/* toast window */}
            {env.IS_ELECTRON && (
                <FloatingTopStatusBar
                    isPresenting={isPresenting}
                    isScreenRecording={roomState.isScreenRecording != null}
                    isDesktopBeingControlled={appState.appState.remoteDesktopBeingControlledStatus != null}
                    remoteRecordingPeer={
                        calcCurrRemoteRecordingPeerAudio(roomState, { noIncludeStopping: true })?.peer?.username || null
                    }
                    remoteRecorderPeer={
                        roomState.remoteRecordingStatus.isBeingRecorded
                            ? roomState.remoteRecordingStatus.recordInitiatorPeer?.username || '?'
                            : null
                    }
                    sessionRecordingPeer={calcSessionRecordingRemotePeers().map((u) => u.username)[0] || null}
                    onEndPresentation={() => dispatch({ type: 'update_media_selection', value: { desktop: '_none' } })}
                    onEndScreenRecording={() => screenRecordingCommand('stop')}
                    onEndRemoteRecording={endCurrRemoteAudioRecord}
                />
            )}

            {/* render audio components (sfu audio) */}
            {(state.clientType === 'listenonly' || !isNullMediaDeviceId(state.mediaSelection.audioOutput)) &&
                Object.keys(roomState.peers)
                    .flatMap((peerSessionId) => roomState.peers[peerSessionId].remoteSfuAudios)
                    .map((remoteTrack) => {
                        // modified from https://github.com/livekit/livekit-react/blob/d94b678dea341f3fc5666f0e3567710df8295bcd/packages/core/src/components/AudioRenderer.tsx
                        // to support `sinkId` prop.
                        const id = `mediaStreamID=${remoteTrack.mediaStreamID},sinkId=${state.mediaSelection.audioOutput}`
                        const sinkId = state.clientType === 'listenonly' ? 'default' : state.mediaSelection.audioOutput
                        return (
                            <ModifiedLiveKitAudioRenderer
                                key={id}
                                id={id}
                                track={remoteTrack}
                                isLocal={false}
                                sinkId={sinkId}
                            />
                        )
                    })}

            {/* render audio components (p2p audio) */}
            {state.clientType === 'listenonly' ? (
                <AudioComponent srcObject={roomState.currPeerAudioOutputStream} sinkId={'_default'} autoPlay />
            ) : (
                <>
                    {!isNullMediaDeviceId(state.mediaSelection.audioOutput) && roomState.currPeerAudioOutputStream && (
                        <AudioComponent
                            srcObject={roomState.currPeerAudioOutputStream}
                            sinkId={state.mediaSelection.audioOutput}
                            autoPlay
                        />
                    )}
                </>
            )}
        </div>
    )
}

function useGalleryLayoutObserver(roomState: RoomContextState) {
    const { t } = useTranslation()
    const { showAppNotification } = useAppNotificationActions()

    const numPeers = Object.keys(roomState.peers).length

    // Change gallery layout based on the number of peers
    useEffect(() => {
        if (sessionViewModeState.mode.galleryLayout.name === 'invite' && numPeers > 0) {
            sessionViewModeState.setGalleryLayout({ name: 'grid', page: 0, reordering: false })
        } else if (numPeers === 0) {
            sessionViewModeState.setGalleryLayout({ name: 'invite' })
        }
    }, [numPeers])

    // Change gallery layout based on incoming screen shares
    const prevPresenters = useRef<Record<string, boolean>>({})
    useEffect(() => {
        const currentPage = sessionViewModeState.currentGalleryPage
        const pinnedPeerId = sessionViewModeState.pinPeerId()

        let foundPinnedPeer = pinnedPeerId ? false : true
        for (const [sessionId, peer] of Object.entries(roomState.peers)) {
            if (pinnedPeerId && sessionId === pinnedPeerId) {
                foundPinnedPeer = true
            }
            if (peer.remoteDesktop && !prevPresenters.current[sessionId]) {
                // New presenter
                const username = peer.displayName || peer.username
                showAppNotification({ title: 'Screen Share', body: `${username} began sharing screen.` })

                if (!sessionViewModeState.hasPin()) {
                    // Switch to pinned mode if nothing is already pinned
                    sessionViewModeState.setGalleryLayout({
                        name: 'pinned',
                        pinnedVideo: { peerSessionId: sessionId, kind: 'screen' },
                        page: currentPage,
                    })

                    if (env.IS_ELECTRON && env.AUTO_ENTER_FULLSCREEN) {
                        sessionViewModeState.enterFullscreen('internal')
                    }
                }
            } else if (!peer.remoteDesktop && prevPresenters.current[sessionId]) {
                // Screen share ended
                const name = getUsername(peer, t)
                if (name) {
                    showAppNotification({ title: 'Screen Share', body: `${name} stopped sharing screen.` })
                }
                if (sessionViewModeState.pinPeerId() === sessionId && sessionViewModeState.pinKind() === 'screen') {
                    // Switch back to grid if it was pinned
                    sessionViewModeState.setGalleryLayout({ name: 'grid', page: currentPage, reordering: false })
                    sessionViewModeState.exitFullscreen()
                }
            }
            prevPresenters.current[sessionId] = peer.remoteDesktop != null
        }

        // Remove pin if the pinned peer is not present anymore
        if (!foundPinnedPeer) {
            sessionViewModeState.setGalleryLayout({ name: 'grid', page: currentPage, reordering: false })
            sessionViewModeState.exitFullscreen()
        }
    }, [roomState.peers])

    // Hide and show gallery based on window size
    useEffect(() => {
        let prevInnerWidth = 0

        function handleWebResize() {
            if (sessionViewModeState.mode.sidebarPoppedOut) {
                if (sessionViewModeState.mode.fullscreen) {
                    sessionViewModeState.setGalleryHidden(false)
                } else {
                    sessionViewModeState.setGalleryHidden(true)
                }
                return
            }

            if (sessionViewModeState.mode.fullscreen) {
                sessionViewModeState.setGalleryHidden(false)
                return
            }

            if (window.innerWidth < 650) {
                sessionViewModeState.setGalleryHidden(true)
            } else {
                if (env.OS === 'darwin' && window.innerWidth - prevInnerWidth > 650) {
                    // If the size changes too quickly, then it is likely from a
                    // fullscreen event; skip.
                    // It is necessary to do this because the fullscreen event (enter-full-screen-user)
                    // comes through the IPC, and it may be slower than the resize.
                    // If the `setGalleryHidden(false)` is called _before_ the enter-full-screen-user
                    // event is handled (see `useViewMode.enterFullscreen()`), then the enter fullscreen
                    // gets canceled.
                } else {
                    sessionViewModeState.setGalleryHidden(false)
                }
            }

            prevInnerWidth = window.innerWidth
        }
        const throttledResize = throttle(handleWebResize, 200, {
            trailing: true,
        })

        window.addEventListener('resize', throttledResize)

        // NOTE: Fixes initial page load on mobile screens
        setTimeout(() => {
            handleWebResize()
        })

        return () => {
            window.removeEventListener('resize', throttledResize)
        }
    }, [])
}

function useExternalEventHandler(
    isPresentingRef: React.MutableRefObject<boolean>,
    onTalkbackStateChange: (isPressed: boolean) => void
) {
    const { appStateRef } = useAppContextRef()
    const { roomStateRef } = useRoomContextRef()
    const { show: showSnackbar } = useRootSnackbar()
    const { toggleWindowSticky } = useMiscRoomActions()
    const { leaveRoomStart, toggleMuteAllPeers } = useRoomActions()

    useEffect(() => {
        const handler = (externalEvent: ExternalEvent) => {
            if (externalEvent.eventName === 'join-room-link') {
                showSnackbar({
                    severity: 'error',
                    message: 'Please leave this room before joining another one.',
                })
                return
            } else if (externalEvent.eventName === 'menu-click') {
                const opcode = externalEvent.value?.opcode
                // TODO: should use openSettingsModal().
                /*if (opcode === 'muse_about') {
                    openSettingsModal('about')
                } else if (opcode === 'muse_accountinfo') {
                    openSettingsModal('account')
                } else if (opcode === 'view_showsessionsettings') {
                    openSettingsModal('session')
                } else if (opcode === 'view_showaudiosettings') {
                    openSettingsModal('audio')
                } else if (opcode === 'view_showvideosettings') {
                    openSettingsModal('video')
                } else if (opcode === 'view_showcommunitypages') {
                    openSettingsModal('community')
                } else if (opcode === 'help_musehelp') {
                    openSettingsModal('about')
                } else if (opcode === 'help_sharingdawaudio') {
                    openSettingsModal('tutorials')
                } else if (opcode === 'help_reportaproblem') {
                    openSettingsModal('about')
                } else if (opcode === 'session_sharescreen') {
                    openSettingsModal('video')
                }*/

                if (opcode === 'session_keepontop') {
                    toggleWindowSticky()
                } else if (opcode === 'session_muteall') {
                    toggleMuteAllPeers()
                } else if (opcode === 'session_leavesession') {
                    leaveRoomStart({ endSession: true })
                }
            } else if (externalEvent.eventName === 'enter-full-screen-user') {
                if (env.OS === 'darwin') {
                    sessionViewModeState.enterFullscreen('external')
                }
            } else if (externalEvent.eventName === 'leave-full-screen-user') {
                if (env.OS === 'darwin') {
                    sessionViewModeState.exitFullscreen()
                }
            }

            // Handle talkback functionality.
            // We don't check which key was pressed because there's only one key (the talkback key)
            // registered to iohook anyway. This "hack" will not work anymore if we have other keys.
            let wasTalkbackKeyDown = false
            if (appStateRef.current.appSettings.talkbackKeyMode === 'hold') {
                // talkback "hold" mode
                if (externalEvent.eventName === 'global-keydown') {
                    if (wasTalkbackKeyDown === false) {
                        onTalkbackStateChange(true)
                        wasTalkbackKeyDown = true
                    }
                } else if (externalEvent.eventName === 'global-keyup') {
                    onTalkbackStateChange(false)
                    wasTalkbackKeyDown = false
                }
            } else {
                // talkback "toggle" mode
                if (externalEvent.eventName === 'global-keydown') {
                    onTalkbackStateChange(!roomStateRef.current.isTalkbackKeyPressed)
                }
            }
        }

        ExternalEventEmitter.on('event', handler)

        return () => {
            ExternalEventEmitter.removeListener('event', handler)
        }
    }, [])
}

function useRemoteRecordingManagement() {
    const [appState] = useAppContext()
    const [userState] = useUserContext()
    const [roomState] = useRoomContext()
    const { roomStateRef } = useRoomContextRef()

    const [currMuseRecordingAudioOutputStream, set_currMuseRecordingAudioOutputStream] = useState<MediaStream | null>(
        null
    )
    // simple-remote-recording management
    useEffect(() => {
        if (!appState.appSettings.remoteRecordingIsSimpleMode) {
            return
        }

        const remoteRecordingStatus = roomState.remoteRecordingStatus

        console.log(`simple-remote-recording: remoteRecordingStatus changed: %O`, remoteRecordingStatus)
        if (
            remoteRecordingStatus?.isRecording &&
            remoteRecordingStatus?.peerSessionId &&
            remoteRecordingStatus?.sourceTrackId
        ) {
            // put the `sourceTrackId` into the `currMuseRecordingAudioOutputStream`.
            const { peerSessionId, sourceTrackId } = remoteRecordingStatus
            console.log(
                `simple-remote-recording: peer recording initiated: peerSessionId=${peerSessionId} ` +
                    `sourceTrackId=${sourceTrackId}`
            )
            const peer = roomStateRef.current.peers[peerSessionId]
            if (peer) {
                console.log(
                    `simple-remote-recording: peer recording initiated: peerSessionId=${peerSessionId} ` +
                        `sourceTrackId=${sourceTrackId}: found peer in the local peers list.`
                )
                const atrack = peer.remoteAudios.find((a) => a.info.deviceId === sourceTrackId)
                if (atrack && atrack.track instanceof MediaStreamTrack) {
                    console.log(
                        `simple-remote-recording: peer recording initiated: peerSessionId=${peerSessionId} ` +
                            `sourceTrackId=${sourceTrackId}: found the corresponding audio track in the peer's atracks list.`
                    )
                    const stream = new MediaStream([atrack.track])
                    console.log(
                        `simple-remote-recording: peer recording initiated: peerSessionId=${peerSessionId} ` +
                            `sourceTrackId=${sourceTrackId}: setting created stream as "currMuseRecordingAudioOutputStream".`
                    )
                    set_currMuseRecordingAudioOutputStream(stream)
                } else if (atrack && atrack.track instanceof FakeMediaStreamTrack) {
                    // no-op
                } else {
                    set_currMuseRecordingAudioOutputStream(null)
                }
            } else {
                set_currMuseRecordingAudioOutputStream(null)
            }
        } else {
            set_currMuseRecordingAudioOutputStream(null)
        }

        if (!remoteRecordingStatus?.isRecording) {
            set_currMuseRecordingAudioOutputStream(null)
            console.log(`simple-remote-recording: detected recording stopped.`)
        }
    }, [roomState.remoteRecordingStatus, appState.appSettings.remoteRecordingIsSimpleMode, roomStateRef])

    const simpleRecordingAudioContextRef = useRef<AudioContext | null>(null)
    useEffect(() => {
        const sinkId = userState.mediaSelection.audioOutputMuseRecording
        if (
            currMuseRecordingAudioOutputStream instanceof MediaStream &&
            sinkId &&
            sinkId !== '_none' &&
            sinkId !== '_undef'
        ) {
            try {
                console.log(
                    `simple-remote-recording: (sinkId=${sinkId}, currMuseRecordingOutputStream.id=` +
                        `${currMuseRecordingAudioOutputStream.id}) init`
                )

                let audioContext = simpleRecordingAudioContextRef.current

                if (audioContext && audioContext instanceof AudioContext) {
                    console.log(
                        `simple-remote-recording: (sinkId=${sinkId}, currMuseRecordingOutputStream.id=` +
                            `${currMuseRecordingAudioOutputStream.id}) calling close() on previous ` +
                            `audioContext.`
                    )
                    audioContext.close()
                }

                audioContext = simpleRecordingAudioContextRef.current = new AudioContext()

                const mp3AudioSource = audioContext.createMediaStreamSource(currMuseRecordingAudioOutputStream)

                const mp3AudioDestination = audioContext.createMediaStreamDestination()
                mp3AudioSource.connect(mp3AudioDestination)

                const outputAudio = new Audio()
                outputAudio.srcObject = mp3AudioDestination.stream
                // @ts-ignore
                outputAudio.setSinkId(sinkId)
                outputAudio.play()

                console.log(
                    `simple-remote-recording: (sinkId=${sinkId}, currMuseRecordingOutputStream.id=` +
                        `${currMuseRecordingAudioOutputStream.id}) setup complete; calling play().`
                )
            } catch (e) {
                console.error(e)
                console.error(
                    `simple-remote-recording: an exception occurred during initialization. ` + serializeError(e)
                )
            }
        } else {
            console.log(`simple-remote-recording: stopping and cleaning up if necessary.`)

            const audioContext = simpleRecordingAudioContextRef.current
            if (audioContext && audioContext instanceof AudioContext) {
                audioContext.close()
            }
            simpleRecordingAudioContextRef.current = null
        }
    }, [userState.mediaSelection.audioOutputMuseRecording, currMuseRecordingAudioOutputStream])
}
